import {
  parsePhoneNumberWithError,
  isValidPhoneNumber,
  type CountryCode,
} from 'libphonenumber-js';

/**
 * Normalize a phone number to E.164 format.
 *
 * @param phone - The raw phone number string.
 * @param country - Optional ISO 3166-1 alpha-2 country code (e.g. "US", "CH").
 * @returns The phone number in E.164 format (e.g. "+14155551234").
 * @throws If the phone number cannot be parsed or is invalid.
 */
export function normalizePhone(phone: string, country?: string): string {
  const parsed = parsePhoneNumberWithError(
    phone,
    country as CountryCode | undefined,
  );

  if (!parsed.isValid()) {
    throw new Error(
      `Invalid phone number: "${phone}"${country ? ` for country ${country}` : ''}`,
    );
  }

  return parsed.format('E.164');
}

/**
 * Check whether a string is a valid E.164 phone number.
 *
 * E.164 format: starts with "+", followed by 1-15 digits.
 *
 * @param phone - The phone number string to validate.
 * @returns True if the string is a valid E.164 phone number.
 */
export function isValidE164(phone: string): boolean {
  // Quick format check before parsing
  if (!/^\+[1-9]\d{1,14}$/.test(phone)) {
    return false;
  }

  return isValidPhoneNumber(phone);
}
